import React, { ReactElement, useMemo, PropsWithChildren } from 'react';
import { Trans } from '@lingui/react/macro';
import { groupBy, keyBy, orderBy } from 'lodash-es';
import { ItemCategory, DocumentType } from '@/types';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { flattenCategories } from '@/utils/categories';
import { DocumentGroupInfo } from '.';
import { useDocumentTypes } from '@/utils/hooks';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { NoResultsCard } from '@/components/Cards';

interface DocumentLike {
    documentTypeId: number;
}

interface DocumentTypeGroupingProps<T extends DocumentLike> {
    items: T[];
    emptyText?: ReactElement | null;
    children: (data: T[], groupInfo: DocumentGroupInfo, key: string) => ReactElement;
}

function getDocumentTypeCategoryMap(
    documentTypes: DocumentType[],
    itemCategories: ItemCategory[]
): { [key: number]: DocumentGroupInfo } {
    const flatCategories = flattenCategories(itemCategories);

    const categoryMap = keyBy(flatCategories, (x) => x.item.id);

    const data = documentTypes.map((x) => {
        var category = x.itemCategoryId ? categoryMap[x.itemCategoryId] : null;

        return {
            category: category?.item ?? null,
            rootCategory: category?.path[0] ?? null,
            documentType: x,
        };
    });

    return keyBy(data, (x) => x.documentType.id);
}

function DocumentTypeGrouping<T extends DocumentLike>({
    children,
    items,
    emptyText = (
        <NoResultsCard>
            <Trans>No documents</Trans>
        </NoResultsCard>
    ),
}: DocumentTypeGroupingProps<T>) {
    const documentTypes = useDocumentTypes();
    const itemCategories = useSelector((state: IState) => state.global.itemCategories);

    const documentTypeMap = useMemo(
        () => getDocumentTypeCategoryMap(documentTypes || [], itemCategories),
        [documentTypes, itemCategories]
    );

    if (!documentTypes) {
        return <LoadingIndicator />;
    }

    const groupedData = groupBy(items, (x) => documentTypeMap[x.documentTypeId].rootCategory?.id ?? 'N/A');

    const data = Object.entries(groupedData).map(([key, value]) => ({
        items: value,
        key: key,
        groupInfo: documentTypeMap[value[0].documentTypeId],
    }));

    if (data.length === 0) {
        return emptyText;
    }

    const sortedData = orderBy(data, [(x) => (x.groupInfo.rootCategory ? 1 : 0), (x) => x.groupInfo.rootCategory?.id ?? 0]);

    return <>{sortedData.map((x) => children(x.items, x.groupInfo, x.key))}</>;
}

export default DocumentTypeGrouping;
